/**
 * 対象までの距離表示。
 * WebXR hit-test 等で実測が入り、ロックゾーン内のターゲットに対してのみ距離を付与する。
 * 推定（bbox）距離は UI に出さない。
 */

import { devConfig } from "./appConfig.js";

/** @type {number | null} */
let lastHardwareRangeM = null;
/** @type {number} */
let lastHardwareSampleAt = 0;

/**
 * WebXR 等から距離サンプルを投入する（ヒット無しのフレームでは呼ばない）
 * @param {number} meters
 */
export function pushHardwareRangeSample(meters) {
  if (typeof meters !== "number" || !Number.isFinite(meters)) return;
  lastHardwareRangeM = meters;
  lastHardwareSampleAt = performance.now();
}

/** @deprecated pushHardwareRangeSample を使用 */
export function setHardwareRangeMeters(meters) {
  if (meters == null || !Number.isFinite(meters)) {
    lastHardwareRangeM = null;
    lastHardwareSampleAt = 0;
    return;
  }
  pushHardwareRangeSample(meters);
}

export function clearHardwareRangeSensor() {
  lastHardwareRangeM = null;
  lastHardwareSampleAt = 0;
}

export function isHardwareRangeFresh() {
  const maxAge = devConfig.detection.rangeHardwareMaxAgeMs ?? 520;
  return (
    lastHardwareRangeM != null &&
    Number.isFinite(lastHardwareRangeM) &&
    performance.now() - lastHardwareSampleAt <= maxAge
  );
}

/** @param {number} m */
function clampRangeM(m) {
  const minM = devConfig.detection.rangeClampMinM ?? 0.35;
  const maxM = devConfig.detection.rangeClampMaxM ?? 24;
  return Math.min(maxM, Math.max(minM, m));
}

/** COCO 主要クラス向けの仮想実寸 (m) — 内部推定用（UI には出さない） */
const CLASS_REF_M = {
  person: 1.72,
  cat: 0.28,
  dog: 0.52,
  bicycle: 1.65,
  car: 1.5,
  motorcycle: 1.15,
  bus: 3.1,
  truck: 3.4,
};

/**
 * @param {string} className
 */
function refHeightForClass(className) {
  const preset =
    CLASS_REF_M[/** @type {keyof typeof CLASS_REF_M} */ (className)];
  if (typeof preset === "number") return preset;
  return devConfig.detection.rangeDefaultRefM ?? 0.55;
}

/**
 * @param {number} bboxH
 * @param {number} frameShortSide
 * @param {string} className
 */
export function estimateRangeMetersFromBbox(bboxH, frameShortSide, className) {
  const cfg = devConfig.detection;
  if (cfg.rangeMode === "dummy_const") {
    const base = cfg.rangeDummyConstM ?? 3.8;
    const wobble =
      (typeof cfg.rangeDummyWobbleM === "number" ? cfg.rangeDummyWobbleM : 0) *
      Math.sin(performance.now() / 900);
    return Math.min(
      cfg.rangeClampMaxM ?? 24,
      Math.max(cfg.rangeClampMinM ?? 0.35, base + wobble),
    );
  }

  const h = Math.max(6, bboxH);
  const fs = Math.max(1, frameShortSide);
  const ref = refHeightForClass(className);
  const k = cfg.rangeCalibrationK ?? 0.38;
  let m = ref * k * (fs / h);
  return clampRangeM(m);
}

/**
 * オーバーレイ・イベント用: 中央ロックゾーン内かつセンサー実測が新鮮なときだけ距離を表示する。
 * @param {{
 *   targetCx: number;
 *   targetCy: number;
 *   zone: { x: number; y: number; side: number };
 * }} p
 */
export function resolveRangeDisplay(p) {
  const { targetCx, targetCy, zone } = p;
  const inZone =
    targetCx >= zone.x &&
    targetCx <= zone.x + zone.side &&
    targetCy >= zone.y &&
    targetCy <= zone.y + zone.side;

  if (inZone && isHardwareRangeFresh() && lastHardwareRangeM != null) {
    return {
      distanceM: clampRangeM(lastHardwareRangeM),
      showDistance: true,
    };
  }

  return { distanceM: null, showDistance: false };
}

/**
 * オーバーレイのクラスラベル用サフィックス（センサー時のみ）
 * @param {{ distanceM: number | null; showDistance: boolean }} r
 */
export function formatRangeDotSuffix(r) {
  if (!r.showDistance || r.distanceM == null || !Number.isFinite(r.distanceM)) {
    return "";
  }
  return ` · ${r.distanceM.toFixed(1)}m`;
}

/**
 * TARGET / LOCK 用の距離断片（先頭スペース付き、無ければ空）
 * @param {{ distanceM: number | null; showDistance: boolean }} r
 */
export function formatTargetDistanceHudFragment(r) {
  if (!r.showDistance || r.distanceM == null || !Number.isFinite(r.distanceM)) {
    return "";
  }
  return ` ${r.distanceM.toFixed(1)}m`;
}
